import { useEffect, useState, type ReactElement, type ReactNode } from "react";

import useNewItemActions from "../NewItemButton/hooks/useNewItemActions";
import { ITEM_TYPE_GROUPS, NewItemEntityType } from "../NewItemButton/new-item-types";
import { useUnifiedRegistry } from "../unified-registry-context";

import LoadingSpinner from "@/components/LoadingSpinner";
import SmartTableCreateDialog from "@/components/SmartTableCreateDialog";
import { ContextMenu, ContextMenuItems, ContextMenuVariant } from "@/components/ui/ContextMenu";
import { getEntityColor, getEntityName, getTypeIcon } from "@/components/UnifiedRegistry/utils";
import { cn } from "@/lib/utils";
import { useRbac } from "@/providers/RbacProvider";
import { EntityType } from "@/queries/folder-entities/types";

interface CreateContextMenuProps {
  children: ReactNode;
  currentFolderId: number | null;
  disabled?: boolean;
  className?: string;
}

interface CreateMenuIconProps {
  type: EntityType;
  className?: string;
}

const CreateMenuIcon = ({ type, className }: CreateMenuIconProps) => (
  <div
    className={cn(
      "flex h-5 w-5 items-center justify-center rounded",
      getEntityColor(type),
      className,
    )}
  >
    {getTypeIcon(type)}
  </div>
);

type ContextMenuIconComponent = ({ className }: { className?: string }) => ReactElement;

const createMenuIconComponent = (type: EntityType): ContextMenuIconComponent =>
  function EntityMenuIcon({ className }) {
    return <CreateMenuIcon type={type} className={className} />;
  };

const CREATE_MENU_ICONS: Record<NewItemEntityType, ContextMenuIconComponent> = {
  [EntityType.FOLDER]: createMenuIconComponent(EntityType.FOLDER),
  [EntityType.PROMPT]: createMenuIconComponent(EntityType.PROMPT),
  [EntityType.WORKFLOW]: createMenuIconComponent(EntityType.WORKFLOW),
  [EntityType.AB_TEST]: createMenuIconComponent(EntityType.AB_TEST),
  [EntityType.SNIPPET]: createMenuIconComponent(EntityType.SNIPPET),
  [EntityType.INPUT_VARIABLE_SET]: createMenuIconComponent(EntityType.INPUT_VARIABLE_SET),
  [EntityType.SKILL_COLLECTION]: createMenuIconComponent(EntityType.SKILL_COLLECTION),
  [EntityType.SMART_TABLE]: createMenuIconComponent(EntityType.SMART_TABLE),
  [EntityType.TOOL]: createMenuIconComponent(EntityType.TOOL),
};

const ASYNC_CREATE_TYPES = new Set<NewItemEntityType>([
  EntityType.WORKFLOW,
  EntityType.PROMPT,
  EntityType.SNIPPET,
  EntityType.INPUT_VARIABLE_SET,
  EntityType.SKILL_COLLECTION,
]);

const CreateContextMenu = ({
  children,
  currentFolderId,
  disabled = false,
  className,
}: CreateContextMenuProps) => {
  const store = useUnifiedRegistry();
  const { actions, isLoading, loadingType, smartTableCreateDialogProps } = useNewItemActions(
    currentFolderId ?? undefined,
    store.createInNewTab,
  );
  const [isContextMenuDisabled, setIsContextMenuDisabled] = useState(disabled);

  const { isCreateDisabled } = useRbac();

  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest("[data-registry-banner]")) {
        setIsContextMenuDisabled(true);
      } else {
        setIsContextMenuDisabled(disabled);
      }
    };

    document.addEventListener("contextmenu", handleContextMenu, true);
    return () => {
      document.removeEventListener("contextmenu", handleContextMenu, true);
    };
  }, [disabled]);

  const contextMenuItems: ContextMenuItems<void>[] = ITEM_TYPE_GROUPS.flatMap(
    (group, groupIndex) => {
      const groupItems: ContextMenuItems<void>[] = group.map(item => ({
        id: `create-${item.type.toLowerCase()}`,
        label: `New ${getEntityName(item.type)}`,
        icon: CREATE_MENU_ICONS[item.type],
        getContent:
          loadingType === item.type
            ? () => (
                <div className="flex min-w-0 items-center text-sm text-gray-900">
                  <LoadingSpinner size={4} className="!mr-2" />
                  <span className="min-w-0 flex-1 whitespace-nowrap">
                    {`Creating ${getEntityName(item.type).toLowerCase()}...`}
                  </span>
                </div>
              )
            : undefined,
        getDisabled: () => isLoading || !!isCreateDisabled?.(item.type),
        preventCloseOnSelect: ASYNC_CREATE_TYPES.has(item.type),
        tooltipContent: isCreateDisabled?.(item.type)
          ? "You do not have permission to create this item"
          : undefined,
        onClick: () => actions[item.type]?.(),
        variant: ContextMenuVariant.PRIMARY,
      }));

      if (groupIndex === ITEM_TYPE_GROUPS.length - 1) {
        return groupItems;
      }

      return [
        ...groupItems,
        {
          id: `divider-${groupIndex}`,
          isDivider: true,
        },
      ];
    },
  );

  if (disabled) {
    return <>{children}</>;
  }

  return (
    <>
      <SmartTableCreateDialog {...smartTableCreateDialogProps} />
      <ContextMenu
        contextMenuItems={contextMenuItems}
        data={undefined}
        modal={store.modal}
        className={className}
        isDisabled={isContextMenuDisabled}
      >
        {children}
      </ContextMenu>
    </>
  );
};

export default CreateContextMenu;
